import { findObjectUseCase } from "../../usecases/object";
import BaseFormPage from "../../base/BaseFormPage";
import { Select, Space } from "antd";
import React from "react";

export default class MultipleSelectHierarchy extends BaseFormPage {
  constructor(props) {
    super(props);
    this.state = {
      region: [],
      office: [],
      division: [],
      section: [],
      unit: [],
      cluster: [],
      selectedRegion: [],
      selectedOffice: [],
      selectedDivision: [],
      selectedSection: [],
      selectedUnit: [],
      selectedCluster: [],
    };
    this.regionInputRef = React.createRef();
  }

  async componentDidMount() {
    const region = await findObjectUseCase().execute("Region", {
      where: { status: "active" },
    });
    this.setState({ region }, () => {
      this.loadRegion();
    });
  }

  getCollectionName() {
    return "Clusters";
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.object !== this.props.object && this.props.object) {
      this.loadRegion();
    }

    if (
      prevState.selectedRegion !== this.state.selectedRegion &&
      this.regionInputRef.current
    ) {
      this.regionInputRef.current.setCustomValidity(
        this.state.selectedRegion.length > 0
          ? ""
          : "Please select at least one region",
      );
    }
  }

  async loadRegion() {
    const { object } = this.props;
    if (object?.regions?.length > 0) {
      this.setState(
        {
          selectedRegion: object.regions,
        },
        () => {
          this.loadOfficeData(object);
        },
      );
    }
  }

  async loadOfficeData(object) {
    const officeQuery = {
      where: {
        status: "active",
        region: { $in: object.regions || this.state.selectedRegion },
      },
    };
    const office = await findObjectUseCase().execute("offices", officeQuery);

    if (object?.offices?.length > 0) {
      this.setState(
        {
          office,
          selectedOffice: object.offices,
        },
        () => {
          this.loadDivisionData(object);
        },
      );
    } else {
      this.setState({ office });
    }
  }

  async loadDivisionData(object) {
    const divisionQuery = {
      where: {
        status: "active",
        office: { $in: object.offices || this.state.selectedOffice },
      },
    };
    const division = await findObjectUseCase().execute(
      "Division",
      divisionQuery,
    );

    if (object?.divisions?.length > 0) {
      this.setState(
        {
          division,
          selectedDivision: object.divisions,
        },
        () => {
          this.loadSectionData(object);
        },
      );
    } else {
      this.setState({ division });
    }
  }

  async loadSectionData(object) {
    const sectionQuery = {
      where: {
        status: "active",
        division: { $in: object.divisions || this.state.selectedDivision },
      },
    };
    const section = await findObjectUseCase().execute("Sections", sectionQuery);

    if (object?.sections?.length > 0) {
      this.setState(
        {
          section,
          selectedSection: object.sections,
        },
        () => {
          this.loadUnitData(object);
        },
      );
    } else {
      this.setState({ section });
    }
  }

  async loadUnitData(object) {
    const unitQuery = {
      where: {
        status: "active",
        section: { $in: object.sections || this.state.selectedSection },
      },
    };
    const unit = await findObjectUseCase().execute("Units", unitQuery);

    if (object?.units?.length > 0) {
      this.setState(
        {
          unit,
          selectedUnit: object.units,
        },
        () => {
          this.loadClusterData(object);
        },
      );
    } else {
      this.setState({ unit });
    }
  }

  async loadClusterData(object) {
    const clusterQuery = {
      where: {
        status: "active",
        unit: { $in: object.units || this.state.selectedUnit },
      },
    };
    const cluster = await findObjectUseCase().execute("Clusters", clusterQuery);

    if (object?.plantilla?.length > 0) {
      this.setState({
        cluster,
        selectedCluster: object.plantilla,
      });
    } else {
      this.setState({ cluster });
    }
  }

  passToParent() {
    const {
      selectedRegion,
      selectedOffice,
      selectedDivision,
      selectedSection,
      selectedUnit,
      selectedCluster,
    } = this.state;

    this.props.handleObjectChange({
      selectedregions: selectedRegion,
      selectedoffices: selectedOffice,
      selecteddivisions: selectedDivision,
      selectedsections: selectedSection,
      selectedunits: selectedUnit,
      selectedplantilla: selectedCluster,
    });
  }

  async handleHierarchyChange(field, value) {
    const selectionField = `selected${field}`;

    const fieldMap = {
      Region: {
        endpoint: "offices",
        params: { region: { $in: value } },
        newState: "office",
        clearFields: [
          "selectedOffice",
          "selectedDivision",
          "selectedSection",
          "selectedUnit",
          "selectedCluster",
        ],
      },
      Office: {
        endpoint: "Division",
        params: { office: { $in: value } },
        newState: "division",
        clearFields: [
          "selectedDivision",
          "selectedSection",
          "selectedUnit",
          "selectedCluster",
        ],
      },
      Division: {
        endpoint: "Sections",
        params: { division: { $in: value } },
        newState: "section",
        clearFields: ["selectedSection", "selectedUnit", "selectedCluster"],
      },
      Section: {
        endpoint: "Units",
        params: { section: { $in: value } },
        newState: "unit",
        clearFields: ["selectedUnit", "selectedCluster"],
      },
      Unit: {
        endpoint: "Clusters",
        params: { unit: { $in: value } },
        newState: "cluster",
        clearFields: ["selectedCluster"],
      },
      Cluster: {
        endpoint: "Clusters",
        params: { id: { $in: value } },
        newState: "",
        clearFields: [],
      },
    };

    // Each level knows: its current selection, where to store fetched data,
    // what endpoint/parentKey to use for fetching its children,
    // and which selection key holds those children.
    const levelChain = [
      {
        selection: "selectedOffice",
        dataKey: "office",
        endpoint: "Division",
        parentKey: "office",
        childSelection: "selectedDivision",
      },
      {
        selection: "selectedDivision",
        dataKey: "division",
        endpoint: "Sections",
        parentKey: "division",
        childSelection: "selectedSection",
      },
      {
        selection: "selectedSection",
        dataKey: "section",
        endpoint: "Units",
        parentKey: "section",
        childSelection: "selectedUnit",
      },
      {
        selection: "selectedUnit",
        dataKey: "unit",
        endpoint: "Clusters",
        parentKey: "unit",
        childSelection: "selectedCluster",
      },
    ];

    if (!fieldMap[field]) {
      this.setState({ [selectionField]: value }, () => this.passToParent());
      return;
    }

    const { endpoint, params, newState, clearFields } = fieldMap[field];

    // Cluster is the last level — no children to cascade, just update selection
    if (clearFields.length === 0) {
      this.setState({ [selectionField]: value }, () => this.passToParent());
      return;
    }

    try {
      if (!value || value.length === 0) {
        // Parent fully cleared — wipe everything downstream
        const clearedState = { [selectionField]: [] };
        clearFields.forEach((f) => {
          clearedState[f] = [];
        });
        if (newState) clearedState[newState] = [];
        this.setState(clearedState, () => this.passToParent());
        return;
      }

      // Step 1: Fetch the immediate child records for the changed parent
      const immediateResult = await findObjectUseCase().execute(endpoint, {
        where: { status: "active", ...params },
      });
      const immediateValidIds = new Set(immediateResult.map((item) => item.id));

      // Step 2: Filter the immediate child selection to only valid IDs
      const immediateSelectionKey = clearFields[0]; // e.g. "selectedOffice"
      const filteredImmediate = (
        this.state[immediateSelectionKey] || []
      ).filter((id) => immediateValidIds.has(id));

      const newState_ = {
        [selectionField]: value,
        [newState]: immediateResult,
        [immediateSelectionKey]: filteredImmediate,
      };

      // Step 3: Walk the rest of the chain level by level.
      // Each iteration fetches children of the current filtered selection,
      // filters the existing child selection down to only still-valid IDs,
      // then moves to the next level using those filtered IDs as parents.
      const startIndex = levelChain.findIndex(
        (l) => l.selection === immediateSelectionKey,
      );

      // startIndex is -1 when the immediate child is the last level (e.g. Unit → Cluster)
      // meaning it only appears as a childSelection, not a selection in the chain.
      // Nothing further to cascade — just set what we have.
      if (startIndex === -1) {
        this.setState(newState_, () => this.passToParent());
        return;
      }

      let currentFilteredIds = filteredImmediate;

      for (let i = startIndex; i < levelChain.length; i++) {
        const level = levelChain[i];
        const nextLevel = levelChain[i + 1];

        if (!currentFilteredIds || currentFilteredIds.length === 0) {
          // No valid parents at this level — wipe this and all remaining levels
          for (let j = i; j < levelChain.length; j++) {
            newState_[levelChain[j].childSelection] = [];
            if (levelChain[j + 1]) {
              newState_[levelChain[j + 1].dataKey] = [];
            }
          }
          break;
        }

        // Fetch children of the current level using the filtered parent IDs
        const childResult = await findObjectUseCase().execute(level.endpoint, {
          where: {
            status: "active",
            [level.parentKey]: { $in: currentFilteredIds },
          },
        });

        const childValidIds = new Set(childResult.map((item) => item.id));

        // Filter the existing child selection to only IDs still present
        const filteredChild = (this.state[level.childSelection] || []).filter(
          (id) => childValidIds.has(id),
        );

        // child results go into the NEXT level's dataKey (not the current)
        if (nextLevel) newState_[nextLevel.dataKey] = childResult;
        newState_[level.childSelection] = filteredChild;

        currentFilteredIds = filteredChild;
      }

      this.setState(newState_, () => this.passToParent());
    } catch (e) {
      console.error(e);
      this.showError(e.message || e);
    }
  }

  render() {
    const fieldLabel = {
      Region: "OCOM",
      Office: "Location",
      Division: "Region",
      Section: "Subport/ IPEE/ Main RIOU",
      Unit: "Division",
      Cluster: "Section/ Unit/ Other Offices",
    };

    const { required } = this.props;

    return (
      <>
        <div className="row">
          {["Region", "Office", "Division", "Section", "Unit", "Cluster"].map(
            (field) => {
              const fieldToLower = field.toLowerCase();
              const isRegionField = field === "Region";
              const showRequired = isRegionField && required;

              return (
                <Space
                  direction="vertical"
                  className={this.props.className}
                  key={field}
                >
                  <label className="fs-sm" style={{ fontSize: "14px" }}>
                    {fieldLabel[field]}{" "}
                    {showRequired && <span className="text-danger">*</span>}
                  </label>

                  <Select
                    mode="multiple"
                    className="mb-2"
                    allowClear
                    size="large"
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    placeholder={`Please Select ${field}`}
                    value={this.state[`selected${field}`]}
                    disabled={
                      this.state[fieldToLower].length === 0 ||
                      this.props.disabled
                    }
                    onChange={(value) =>
                      this.handleHierarchyChange(field, value)
                    }
                    options={this.state[fieldToLower]?.map((item) => ({
                      label: item.name,
                      value: item.id,
                    }))}
                    style={{
                      width: "100%",
                    }}
                  />

                  {/* Hidden input for HTML5 validation on Region field */}
                  {isRegionField && required && (
                    <input
                      type="text"
                      required
                      disabled={
                        this.state[fieldToLower].length === 0 ||
                        this.props.disabled
                      }
                      value={
                        this.state.selectedRegion.length > 0 ? "valid" : ""
                      }
                      onChange={() => {}}
                      style={{
                        opacity: 0,
                        height: 0,
                        margin: "-1rem",
                        position: "absolute",
                        pointerEvents: "none",
                      }}
                      tabIndex={-1}
                    />
                  )}
                </Space>
              );
            },
          )}
        </div>
      </>
    );
  }
}
